// Dashboard de um cliente no Memor.IA (página Análise de Clientes).
// Busca /api/reunioes/analise-clientes/detalhe/?cliente=<nome> e mostra: indicadores, evolução
// da nota, última reunião com próximos passos, riscos e oportunidades, notas mais baixas e mais
// altas, objeções recorrentes, operadores que atenderam e o histórico completo.
// Clicar numa reunião abre a análise completa (ReuniaoAnaliseModal).
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import ReuniaoAnaliseModal from './ReuniaoAnaliseModal';
import MemoraClienteChat from './MemoraClienteChat';
import { labelTipoReuniao } from '../utils/competencias';

const API = process.env.REACT_APP_API_BASE;
const COR_SERIE = '#00205b';
const fmtNota = (n) => (n === null || n === undefined ? '-' : Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
const fmtData = (d) => {
  if (!d) return '-';
  const p = String(d).slice(0, 10).split('-');
  return p.length < 3 ? d : `${p[2]}/${p[1]}/${p[0]}`;
};
const badgeNota = (n) => {
  if (n === null || n === undefined) return 'bg-secondary';
  if (n >= 8) return 'bg-success';
  if (n >= 6) return 'bg-primary';
  if (n >= 4) return 'bg-warning text-dark';
  return 'bg-danger';
};
const media = (l) => {
  const ns = l.filter((x) => x !== null && x !== undefined);
  return ns.length ? ns.reduce((a, b) => a + b, 0) / ns.length : null;
};
const URGENCIA = {
  alta: { classe: 'bg-danger', rotulo: 'Alta', peso: 0 },
  media: { classe: 'bg-warning text-dark', rotulo: 'Média', peso: 1 },
  baixa: { classe: 'bg-secondary', rotulo: 'Baixa', peso: 2 },
};
const textoItem = (x, ...campos) => {
  if (typeof x === 'string') return x;
  for (const c of campos) if (x && x[c]) return x[c];
  return '';
};

function Kpi({ icone, rotulo, valor, extra, classe = 'text-muted', dica }) {
  return (
    <div className="col-6 col-md-4 col-xl-2">
      <div className="card border-0 shadow-sm h-100" title={dica}>
        <div className="card-body py-3">
          <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: 600 }}>
            <i className={`fas ${icone} me-1 ${classe}`}></i>{rotulo}
          </div>
          <div className="fw-bold d-flex align-items-baseline gap-1" style={{ fontSize: '1.45rem', color: '#212529', fontVariantNumeric: 'tabular-nums' }}>
            {valor}{extra}
          </div>
        </div>
      </div>
    </div>
  );
}

function Bloco({ icone, titulo, children, acao, corIcone = 'text-muted' }) {
  return (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="fw-bold mb-0" style={{ color: '#212529' }}>
            <i className={`fas ${icone} me-2 ${corIcone}`}></i>{titulo}
          </h6>
          {acao}
        </div>
        {children}
      </div>
    </div>
  );
}

function Vazio({ children }) {
  return <div className="text-muted" style={{ fontSize: '0.85rem' }}>{children}</div>;
}

function ChipReuniao({ r, onAbrir }) {
  return (
    <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" style={{ fontSize: '0.75rem' }}
      onClick={() => onAbrir(r.codigo)} title="Ver análise completa">
      {fmtData(r.data)} · {r.codigo}
    </button>
  );
}

function ListaPontos({ itens, onAbrir, vazio }) {
  if (!itens.length) return <Vazio>{vazio}</Vazio>;
  return (
    <div className="d-flex flex-column gap-2" style={{ maxHeight: 360, overflowY: 'auto' }}>
      {itens.map((p, i) => {
        const u = URGENCIA[p.urgencia];
        return (
          <div key={i} className="border rounded p-2" style={{ fontSize: '0.83rem' }}>
            <div className="d-flex justify-content-between align-items-center mb-1 gap-2">
              {u ? <span className={`badge ${u.classe}`}>{u.rotulo}</span> : <span />}
              <ChipReuniao r={p.reuniao} onAbrir={onAbrir} />
            </div>
            <div style={{ color: '#212529', lineHeight: 1.45 }}>{p.descricao}</div>
          </div>
        );
      })}
    </div>
  );
}

function TooltipNota({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const r = payload[0].payload;
  return (
    <div className="bg-white border rounded shadow-sm p-2" style={{ fontSize: '0.8rem', maxWidth: 260 }}>
      <div className="fw-bold" style={{ color: '#212529' }}>Nota {fmtNota(r.nota)}</div>
      <div className="text-muted">{fmtData(r.data)} · {labelTipoReuniao(r.tipo)}</div>
      <div style={{ color: '#212529' }}>{r.assunto}</div>
    </div>
  );
}

function ListaNotas({ itens, onAbrir }) {
  if (!itens.length) return <Vazio>Sem reuniões com nota.</Vazio>;
  return (
    <div className="d-flex flex-column gap-2">
      {itens.map((r) => (
        <div key={r.codigo} className="d-flex align-items-start gap-2 border rounded p-2" style={{ cursor: 'pointer', fontSize: '0.83rem' }}
          onClick={() => onAbrir(r.codigo)}>
          <span className={`badge ${badgeNota(r.nota)}`} style={{ fontSize: '0.85rem', minWidth: 42 }}>{fmtNota(r.nota)}</span>
          <div className="flex-grow-1">
            <div style={{ color: '#212529' }}>{r.assunto || r.codigo}</div>
            <div className="text-muted" style={{ fontSize: '0.75rem' }}>{fmtData(r.data)} · {labelTipoReuniao(r.tipo)}</div>
            {r.resumo && <div className="text-muted mt-1" style={{ fontSize: '0.78rem', lineHeight: 1.4 }}>{r.resumo}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function MemoraClienteDashboard({ cliente, onVoltar }) {
  const [reunioes, setReunioes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [codigoAberto, setCodigoAberto] = useState(null);
  const [chatAberto, setChatAberto] = useState(false);
  const fecharChat = useCallback(() => setChatAberto(false), []);
  const fecharReuniao = useCallback(() => setCodigoAberto(null), []);

  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    setErro(null);
    axios.get(`${API}/api/reunioes/analise-clientes/detalhe/`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('access')}` }, params: { cliente },
    }).then((r) => { if (vivo) setReunioes(r.data?.reunioes || []); })
      .catch((e) => { if (vivo) { setErro(e.response?.data?.erro || 'Não foi possível carregar o cliente.'); setReunioes([]); } })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [cliente]);

  const d = useMemo(() => {
    const comNota = reunioes.filter((r) => r.nota !== null && r.nota !== undefined);
    const ultima = reunioes[0] || null;
    const anterior = reunioes[1] || null;
    const riscos = [];
    const oportunidades = [];
    reunioes.forEach((r) => {
      (r.riscos || []).forEach((p) => riscos.push({ ...p, reuniao: r }));
      (r.oportunidades || []).forEach((p) => oportunidades.push({ ...p, reuniao: r }));
    });
    const porUrgencia = (a, b) => (URGENCIA[a.urgencia]?.peso ?? 3) - (URGENCIA[b.urgencia]?.peso ?? 3)
      || String(b.reuniao.data || '').localeCompare(String(a.reuniao.data || ''));
    riscos.sort(porUrgencia);
    oportunidades.sort(porUrgencia);

    const objecoes = {};
    reunioes.forEach((r) => (r.objecoes || []).forEach((o) => {
      const t = textoItem(o, 'objecao', 'descricao', 'texto').trim();
      if (t) objecoes[t] = (objecoes[t] || 0) + 1;
    }));
    const ops = {};
    reunioes.forEach((r) => (r.operadores || []).forEach((o) => {
      const k = o.nome || '—';
      if (!ops[k]) ops[k] = { nome: k, notas: [], reunioes: 0 };
      ops[k].reunioes += 1;
      if (o.nota !== null && o.nota !== undefined) ops[k].notas.push(o.nota);
    }));
    const setores = [...new Set(reunioes.flatMap((r) => r.setores || []))];
    const contato = reunioes.map((r) => r.contato).find((c) => c && c.nome);
    return {
      ultima, anterior, riscos, oportunidades, setores, contato,
      dominio: reunioes.map((r) => r.numero_dominio).find(Boolean),
      media: media(comNota.map((r) => r.nota)),
      maior: comNota.length ? Math.max(...comNota.map((r) => r.nota)) : null,
      menor: comNota.length ? Math.min(...comNota.map((r) => r.nota)) : null,
      maisBaixas: [...comNota].sort((a, b) => a.nota - b.nota).slice(0, 3),
      maisAltas: [...comNota].sort((a, b) => b.nota - a.nota).slice(0, 3),
      riscosAltos: riscos.filter((p) => p.urgencia === 'alta').length,
      objecoes: Object.entries(objecoes).sort((a, b) => b[1] - a[1]),
      operadores: Object.values(ops).map((o) => ({ ...o, media: media(o.notas) })).sort((a, b) => b.reunioes - a.reunioes),
      serie: [...comNota].reverse().map((r) => ({ ...r, rotulo: fmtData(r.data).slice(0, 5) })),
    };
  }, [reunioes]);

  const delta = d.ultima && d.anterior && d.ultima.nota !== null && d.anterior.nota !== null
    ? d.ultima.nota - d.anterior.nota : null;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h4 className="fw-bold mb-1" style={{ color: '#212529' }}>{cliente}</h4>
          <div className="d-flex flex-wrap align-items-center gap-2 text-muted" style={{ fontSize: '0.83rem' }}>
            {d.dominio && <span>Domínio {d.dominio}</span>}
            {d.contato && <span><i className="fas fa-user me-1"></i>{d.contato.nome}{d.contato.cargo ? ` · ${d.contato.cargo}` : ''}</span>}
            {d.setores.map((s) => <span key={s} className="badge rounded-pill bg-light text-dark border">{s}</span>)}
          </div>
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-sm btn-primary" onClick={() => setChatAberto(true)} disabled={!reunioes.length}>
            <i className="fas fa-comments me-1"></i>Perguntar à IA
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onVoltar}>
            <i className="fas fa-arrow-left me-1"></i>Todos os clientes
          </button>
        </div>
      </div>

      {erro && <div className="alert alert-danger py-2">{erro}</div>}
      {carregando ? (
        <div className="text-center text-muted py-5"><span className="spinner-border spinner-border-sm me-2"></span>Carregando o cliente...</div>
      ) : !reunioes.length ? (
        <div className="text-center text-muted py-5">Nenhuma reunião analisada deste cliente.</div>
      ) : (
        <>
          <div className="row g-3 mb-3">
            <Kpi icone="fa-video" rotulo="Reuniões" valor={reunioes.length} />
            <Kpi icone="fa-star" rotulo="Nota média" valor={fmtNota(d.media)} classe="text-warning" />
            <Kpi icone="fa-clock-rotate-left" rotulo="Última nota" valor={fmtNota(d.ultima?.nota)}
              extra={delta !== null && Math.abs(delta) >= 0.05 ? (
                <span className={delta > 0 ? 'text-success' : 'text-danger'} style={{ fontSize: '0.8rem' }}>
                  <i className={`fas fa-arrow-${delta > 0 ? 'up' : 'down'} me-1`}></i>{fmtNota(Math.abs(delta))}
                </span>
              ) : null}
              dica="Comparada à reunião anterior" />
            <Kpi icone="fa-arrow-trend-up" rotulo="Maior nota" valor={fmtNota(d.maior)} classe="text-success" />
            <Kpi icone="fa-arrow-trend-down" rotulo="Menor nota" valor={fmtNota(d.menor)} classe="text-danger" />
            <Kpi icone="fa-triangle-exclamation" rotulo="Riscos (alta)" valor={`${d.riscosAltos}/${d.riscos.length}`} classe="text-danger"
              dica="Riscos de urgência alta / total de riscos levantados" />
          </div>

          <div className="row g-3 mb-3">
            <div className="col-lg-7">
              <Bloco icone="fa-chart-line" titulo="Evolução da nota">
                {d.serie.length < 2 ? (
                  <Vazio>É preciso ao menos duas reuniões com nota para mostrar a evolução.</Vazio>
                ) : (
                  <div style={{ width: '100%', height: 260 }}>
                    <ResponsiveContainer>
                      <LineChart data={d.serie} margin={{ top: 10, right: 16, bottom: 0, left: -16 }}>
                        <CartesianGrid stroke="#eef1f6" vertical={false} />
                        <XAxis dataKey="rotulo" tick={{ fontSize: 11, fill: '#6c757d' }} axisLine={{ stroke: '#dee2e6' }} tickLine={false} />
                        <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} tick={{ fontSize: 11, fill: '#6c757d' }} axisLine={false} tickLine={false} />
                        <ReferenceLine y={6} stroke="#adb5bd" strokeDasharray="4 4"
                          label={{ value: 'atenção', position: 'insideTopRight', fontSize: 10, fill: '#6c757d' }} />
                        <Tooltip content={<TooltipNota />} cursor={{ stroke: '#ced4da' }} />
                        <Line type="monotone" dataKey="nota" stroke={COR_SERIE} strokeWidth={2}
                          dot={{ r: 4, fill: COR_SERIE, stroke: '#ffffff', strokeWidth: 2 }}
                          activeDot={{ r: 6, stroke: '#ffffff', strokeWidth: 2, onClick: (_, p) => p?.payload && setCodigoAberto(p.payload.codigo) }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Bloco>
            </div>
            <div className="col-lg-5">
              <Bloco icone="fa-calendar-check" titulo="Última reunião"
                acao={<ChipReuniao r={d.ultima} onAbrir={setCodigoAberto} />}>
                <div className="d-flex align-items-start gap-2 mb-2">
                  <span className={`badge ${badgeNota(d.ultima.nota)}`} style={{ fontSize: '0.9rem' }}>{fmtNota(d.ultima.nota)}</span>
                  <div>
                    <div className="fw-semibold" style={{ color: '#212529' }}>{d.ultima.assunto || d.ultima.codigo}</div>
                    <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                      {labelTipoReuniao(d.ultima.tipo)}{d.ultima.interesse ? ` · interesse ${d.ultima.interesse}` : ''}
                      {d.ultima.analisado_por ? ` · analisada por ${d.ultima.analisado_por}` : ''}
                    </div>
                  </div>
                </div>
                {d.ultima.resumo && <p className="mb-2" style={{ fontSize: '0.83rem', color: '#212529', lineHeight: 1.5 }}>{d.ultima.resumo}</p>}
                {d.ultima.dor_principal && (
                  <div className="mb-2" style={{ fontSize: '0.8rem' }}>
                    <span className="text-muted fw-semibold">Dor principal: </span><span style={{ color: '#212529' }}>{d.ultima.dor_principal}</span>
                  </div>
                )}
                <div className="text-muted fw-semibold mb-1" style={{ fontSize: '0.75rem' }}>PRÓXIMOS PASSOS</div>
                {(d.ultima.proximos_passos || []).length ? (
                  <ul className="mb-0 ps-3" style={{ fontSize: '0.82rem' }}>
                    {d.ultima.proximos_passos.map((p, i) => (
                      <li key={i} style={{ color: '#212529' }}>
                        {textoItem(p, 'acao', 'descricao')}
                        <span className="text-muted">{p.responsavel ? ` — ${p.responsavel}` : ''}{p.prazo ? ` · ${p.prazo}` : ''}</span>
                      </li>
                    ))}
                  </ul>
                ) : <Vazio>Nenhum próximo passo registrado.</Vazio>}
              </Bloco>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-lg-6">
              <Bloco icone="fa-triangle-exclamation" corIcone="text-danger" titulo={`Riscos (${d.riscos.length})`}>
                <ListaPontos itens={d.riscos} onAbrir={setCodigoAberto} vazio="Nenhum risco levantado nas reuniões." />
              </Bloco>
            </div>
            <div className="col-lg-6">
              <Bloco icone="fa-lightbulb" corIcone="text-success" titulo={`Oportunidades (${d.oportunidades.length})`}>
                <ListaPontos itens={d.oportunidades} onAbrir={setCodigoAberto} vazio="Nenhuma oportunidade levantada nas reuniões." />
              </Bloco>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-lg-6">
              <Bloco icone="fa-arrow-trend-down" corIcone="text-danger" titulo="Notas mais baixas">
                <ListaNotas itens={d.maisBaixas} onAbrir={setCodigoAberto} />
              </Bloco>
            </div>
            <div className="col-lg-6">
              <Bloco icone="fa-arrow-trend-up" corIcone="text-success" titulo="Notas mais altas">
                <ListaNotas itens={d.maisAltas} onAbrir={setCodigoAberto} />
              </Bloco>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-lg-6">
              <Bloco icone="fa-comment-slash" titulo="Objeções e queixas do cliente">
                {d.objecoes.length ? (
                  <div className="d-flex flex-wrap gap-2">
                    {d.objecoes.map(([t, n]) => (
                      <span key={t} className="badge rounded-pill bg-light text-dark border" style={{ fontSize: '0.78rem', fontWeight: 500, whiteSpace: 'normal', textAlign: 'left' }}>
                        {t}{n > 1 ? <strong className="ms-1">×{n}</strong> : null}
                      </span>
                    ))}
                  </div>
                ) : <Vazio>Nenhuma objeção registrada.</Vazio>}
              </Bloco>
            </div>
            <div className="col-lg-6">
              <Bloco icone="fa-users" titulo="Quem atendeu">
                {d.operadores.length ? (
                  <table className="table table-sm mb-0" style={{ fontSize: '0.83rem' }}>
                    <thead><tr className="text-muted"><th>Operador</th><th className="text-center">Reuniões</th><th className="text-center">Nota média</th></tr></thead>
                    <tbody>
                      {d.operadores.map((o) => (
                        <tr key={o.nome}>
                          <td style={{ color: '#212529' }}>{o.nome}</td>
                          <td className="text-center" style={{ fontVariantNumeric: 'tabular-nums' }}>{o.reunioes}</td>
                          <td className="text-center"><span className={`badge ${badgeNota(o.media)}`}>{fmtNota(o.media)}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <Vazio>Nenhum operador avaliado.</Vazio>}
              </Bloco>
            </div>
          </div>

          <Bloco icone="fa-list" titulo="Histórico de reuniões">
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle mb-0" style={{ fontSize: '0.83rem' }}>
                <thead><tr className="text-muted"><th>Data</th><th>Reunião</th><th>Tipo</th><th>Setor</th><th className="text-center">Nota</th><th className="text-center">Riscos</th><th className="text-center">Oportunidades</th><th></th></tr></thead>
                <tbody>
                  {reunioes.map((r) => (
                    <tr key={r.codigo} style={{ cursor: 'pointer' }} onClick={() => setCodigoAberto(r.codigo)}>
                      <td className="text-nowrap">{fmtData(r.data)}</td>
                      <td>
                        <div style={{ color: '#212529' }}>{r.assunto || '-'}</div>
                        <div className="text-muted" style={{ fontSize: '0.72rem' }}>{r.codigo}</div>
                      </td>
                      <td className="text-nowrap">{labelTipoReuniao(r.tipo)}</td>
                      <td>{(r.setores || []).join(', ') || '-'}</td>
                      <td className="text-center"><span className={`badge ${badgeNota(r.nota)}`}>{fmtNota(r.nota)}</span></td>
                      <td className="text-center" style={{ fontVariantNumeric: 'tabular-nums' }}>{(r.riscos || []).length}</td>
                      <td className="text-center" style={{ fontVariantNumeric: 'tabular-nums' }}>{(r.oportunidades || []).length}</td>
                      <td className="text-end text-muted"><i className="fas fa-up-right-from-square" title="Ver análise completa"></i></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Bloco>
        </>
      )}

      {codigoAberto && <ReuniaoAnaliseModal codigo={codigoAberto} onClose={fecharReuniao} />}
      {chatAberto && <MemoraClienteChat cliente={cliente} onClose={fecharChat} />}
    </div>
  );
}
